import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

interface LoadingContextType {
    showLoading: () => void;
    hideLoading: () => void;
    showThinking: () => void;
    hideThinking: () => void;
    isLoading: boolean;
    isThinking: boolean;
}

const LoadingContext = createContext<LoadingContextType>({
    showLoading: () => { },
    hideLoading: () => { },
    showThinking: () => { },
    hideThinking: () => { },
    isLoading: false,
    isThinking: false,
});

export const useLoading = () => useContext(LoadingContext);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [loadingCount, setLoadingCount] = useState(0);
    const [thinkingCount, setThinkingCount] = useState(0);

    const showLoading = useCallback(() => setLoadingCount((c) => c + 1), []);
    const hideLoading = useCallback(() => setLoadingCount((c) => Math.max(c - 1, 0)), []);

    const showThinking = useCallback(() => setThinkingCount((c) => c + 1), []);
    const hideThinking = useCallback(() => setThinkingCount((c) => Math.max(c - 1, 0)), []);

    const isLoading = useMemo(() => loadingCount > 0, [loadingCount]);
    const isThinking = useMemo(() => thinkingCount > 0, [thinkingCount]);

    return (
        <LoadingContext.Provider
            value={{ showLoading, hideLoading, showThinking, hideThinking, isLoading, isThinking }}
        >
            {children}

            {/* Loading overlay (spinner) */}
            {isLoading && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: "rgba(255,255,255,0.7)",
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* Thinking overlay (moving smiley + animated dots) */}
            {isThinking && <ThinkingOverlay />}
        </LoadingContext.Provider>
    );
};


const ThinkingOverlay: React.FC = () => (
    <Box sx={thinkingOverlaySx}>
        <Box sx={thinkingBoxSx}>
            <Typography
                aria-label="thinking"
                sx={{
                    fontSize: 48,
                    lineHeight: 1,
                    display: "inline-block",
                    animation: "bob 2.8s ease-in-out infinite",
                }}
            >
                🤔
            </Typography>

            <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
           
                <Box
                    component="span"
                    sx={{
                        ml: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                    }}
                >
                    {[0, 0.2, 0.4].map((delay) => (
                        <Box
                            key={delay}
                            component="span"
                            sx={{
                                mx: 0.25,
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: "text.primary",
                                animation: "blink 1.2s infinite",
                                animationDelay: `${delay}s`,
                            }}
                        />
                    ))}
                </Box>
            </Typography>
        </Box>
    </Box>
);


const thinkingOverlaySx = {
    position: "fixed",
    inset: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    bgcolor: "rgba(255,255,255,0.7)",
    zIndex: 10000,
    "@keyframes floatY": {
        "0%, 100%": { transform: "translateY(0px)" },
        "50%": { transform: "translateY(-10px)" },
    },
    "@keyframes wanderX": {
        "0%, 100%": { transform: "translateX(0px)" },
        "50%": { transform: "translateX(8px)" },
    },
    "@keyframes blink": {
        "0%": { opacity: 0.2 },
        "50%": { opacity: 1 },
        "100%": { opacity: 0.2 },
    },
    "@keyframes bob": {
        "0%,100%": { transform: "translate(0,0) rotate(0deg)" },
        "25%": { transform: "translate(4px,-6px) rotate(-3deg)" },
        "50%": { transform: "translate(0,-10px) rotate(0deg)" },
        "75%": { transform: "translate(-4px,-6px) rotate(3deg)" },
    },
};

const thinkingBoxSx = {
    px: 3,
    py: 2,
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    gap: 2,
    animation: "floatY 3s ease-in-out infinite, wanderX 4s ease-in-out infinite",
};
